import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, Alert, Image } from 'react-native';
import { SettingsIcon, PhoneIcon, PersonIcon, CloseIcon, CheckCircleIcon, ErrorIcon } from '../icons';
import { styles, colors } from '../styles';
import { iniciarLigacao } from '../services';
import CallHistory from './CallHistory';

const DialerInterface = ({ contactInfo, onSettingsClick, userId, ramal, systemKey }) => {
  const [loading, setLoading] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);
  const [settingsHover, setSettingsHover] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 10000);
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const handleIniciarLigacao = async () => {
    if (!contactInfo?.contactId || !contactInfo?.numero) {
      showNotification('Informações do contato não disponíveis', 'error');
      return;
    }

    if (!ramal) {
      showNotification('Ramal não configurado', 'error');
      return;
    }

    if (!userId) {
      showNotification('Sistema não inicializado', 'error');
      return;
    }

    setLoading(true);
    setNotification(null);

    try {
      await iniciarLigacao(userId, contactInfo.numero);
      showNotification('Ligação iniciada com sucesso!', 'success');
    } catch (err) {
      let errorMessage = 'Não foi possível iniciar a ligação. Tente novamente.';
      if (err.data && err.data.error) {
        errorMessage = err.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Conteúdo Principal */}
      <View style={{ flex: 1, flexDirection: 'column' }}>
        <View style={{ padding: 12 }}>
          {/* Notificação */}
          {notification && (
            <View style={{
              marginBottom: 16,
              padding: 18,
              borderRadius: 8,
              backgroundColor: notification.type === 'success' ? '#D1FAE5' : '#FEE2E2',
              borderWidth: 1,
              borderColor: notification.type === 'success' ? colors.success : colors.error,
              position: 'relative',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}>
              <TouchableOpacity
                onPress={closeNotification}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  padding: 4,
                  zIndex: 1,
                  borderRadius: 4,
                }}
                activeOpacity={0.7}
              >
                <CloseIcon 
                  size={18} 
                  color={notification.type === 'success' ? colors.success : colors.error} 
                />
              </TouchableOpacity>
              
              <View style={{ 
                flexDirection: 'row', 
                alignItems: 'flex-start',
                paddingRight: 30,
              }}>
                <View style={{ 
                  marginRight: 12, 
                  marginTop: 2,
                  width: 24,
                  height: 24,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {notification.type === 'success' ? (
                    <CheckCircleIcon size={24} color={colors.success} />
                  ) : (
                    <ErrorIcon size={24} color={colors.error} />
                  )}
                </View>
                
                <View style={{ flex: 1 }}>
                  <Text style={{
                    color: notification.type === 'success' ? colors.success : colors.error,
                    fontSize: 14,
                    lineHeight: 20,
                    fontWeight: '500',
                  }}>
                    {notification.message}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {contactInfo ? (
          <View>
            {/* Card do Contato - Estilo melhorado */}
            <View style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              marginBottom: 20,
              padding: 20,
              backgroundColor: colors.bgPrimary,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: colors.borderLight,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.05,
              shadowRadius: 4,
              elevation: 2,
            }}>
              {/* Foto de Perfil */}
              {contactInfo.linkImagem ? (
                <Image
                  source={{ uri: contactInfo.linkImagem }}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 30,
                    marginRight: 16,
                    backgroundColor: colors.borderLight
                  }}
                />
              ) : (
                <View style={{
                  width: 60,
                  height: 60,
                  borderRadius: 30,
                  marginRight: 16,
                  backgroundColor: colors.borderLight,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{ fontSize: 24, color: colors.textPrimary, fontWeight: '600' }}>
                    {(contactInfo.nome || '?').charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}

              {/* Informações do Contato */}
              <View style={{ flex: 1 }}>
                {/* Nome Principal */}
                <Text style={[styles.textLarge, { 
                  marginBottom: 6, 
                  fontSize: 18, 
                  fontWeight: '600',
                  color: colors.textPrimary
                }]}>
                  {contactInfo.nome || 'Sem nome'}
                </Text>

                {/* Nome Secundário */}
                {contactInfo.nomeSecundario && (
                  <Text style={[styles.textSmall, { 
                    marginBottom: 4, 
                    opacity: 0.8, 
                    fontSize: 14,
                    fontWeight: '500'
                  }]}>
                    {contactInfo.nomeSecundario}
                  </Text>
                )}

                {/* Número */}
                {contactInfo.numero && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                    <View style={{ marginRight: 6, opacity: 0.6 }}>
                      <PhoneIcon size={14} color={colors.textSecondary} />
                    </View>
                    <Text style={[styles.textSmall, { 
                      opacity: 0.7, 
                      fontSize: 14,
                      fontWeight: '500'
                    }]}>
                      {contactInfo.numero}
                    </Text>
                  </View>
                )}
              </View>
            </View>

            {/* Botão de Ligação - Melhorado */}
            <TouchableOpacity
              style={[
                styles.button,
                buttonHover && {
                  ...styles.buttonHover,
                  opacity: 0.9,
                },
                loading && { opacity: 0.6 },
                { 
                  padding: 16,
                  borderRadius: 8,
                }
              ]}
              onPress={handleIniciarLigacao}
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
              disabled={loading}
              activeOpacity={1}
            >
              {loading ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <ActivityIndicator size="small" color={colors.buttonText} />
                  <Text style={styles.buttonText}>Iniciando ligação...</Text>
                </View>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <PhoneIcon size={20} color={colors.buttonText} />
                  <Text style={[styles.buttonText, { fontSize: 16, fontWeight: '600' }]}>
                    Iniciar Ligação
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
        ) : (
            <View style={{ 
              flex: 1, 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 300,
              paddingVertical: 40
            }}>
              {/* Ícone acima da mensagem */}
              <View style={{
                width: 80,
                height: 80,
                borderRadius: 40,
                backgroundColor: '#F8F9FA',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
                borderWidth: 1,
                borderColor: colors.borderLight,
              }}>
                <PersonIcon size={48} color={colors.borderLight} />
              </View>
              
              <Text style={[styles.text, { 
                marginBottom: 8,
                fontSize: 18,
                fontWeight: '600'
              }]}>
                Nenhum contato selecionado!
              </Text>
              <Text style={[styles.textSmall, { 
                opacity: 0.7, 
                marginBottom: 32,
                textAlign: 'center',
                paddingHorizontal: 20,
                fontSize: 14
              }]}>
                Abra um chat para realizar uma ligação.
              </Text>
              
              {/* Botão de Configurações */}
              <TouchableOpacity
                style={[
                  styles.button,
                  settingsHover && {
                    ...styles.buttonHover,
                    opacity: 0.9,
                  },
                  { 
                    minWidth: 180,
                    padding: 14,
                    borderRadius: 8,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 2,
                  }
                ]}
                onPress={onSettingsClick}
                onMouseEnter={() => setSettingsHover(true)}
                onMouseLeave={() => setSettingsHover(false)}
                activeOpacity={1}
              >
                <SettingsIcon size={18} color={colors.buttonText} />
                <Text style={[styles.buttonText, { fontSize: 15 }]}>
                  Configurar Ramal
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Rodapé - Histórico */}
        {contactInfo?.numero && (
          <View style={{ flex: 1, minHeight: 0 }}>
            <CallHistory 
              userId={userId} 
              numero={contactInfo.numero}
            />
          </View>
        )}
      </View>
    </View>
  );
};

export default DialerInterface;

